import * as React from 'react';
import { NavLink, Link } from 'react-router-dom';
import driverinfosecurity from '../../utils/DriverInfoSecurity';
import * as i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { LANGUAGE } from '../../constants/storageKeys';
import { flags } from '../../constants/flags';
import { Language } from '../interfaces/interfaces';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useLanguages, useLanguagesMatch } from '../../hooks/useLanguages';
import { useCountry } from '../../context/CountryContext';

type ChooseProps = { value: string; current: string };
const Choose = ({ value, current }: ChooseProps): JSX.Element | null => (
    value === current ? <span> ({i18next.t('navbar.choosen')})</span> : null
);

function NavMenu() {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const { country } = useCountry();
    const { data: languageCodes } = useLanguages();
    const languagesMatch = useLanguagesMatch();
    const currentLang = window.localStorage.getItem(LANGUAGE) ?? 'sv-SE';
    const open = Boolean(anchorEl);

    React.useEffect(() => {
        if (languageCodes && languageCodes.length > 0 && !languagesMatch) {
            throw new Error('Language mismatch between client and server!');
        }
    }, [languageCodes, languagesMatch]);

    function changeLanguage(countryCode: string) {
        i18next.changeLanguage(countryCode);
        localStorage[LANGUAGE] = countryCode;
        moment.locale(countryCode);
        window.location.reload();
    }

    function renderLanguage(lang: Language): React.ReactNode {
        return (
            <MenuItem key={lang.cultureCode}
                onClick={() => { setAnchorEl(null); changeLanguage(lang.cultureCode); }}
                sx={{ color: 'white', '&:hover': { color: 'secondary.light', bgcolor: 'transparent' } }}>
                <span className={lang.flag + 'flagsIcon'}></span>
                {i18next.t(lang.languageTag)} <Choose value={lang.cultureCode} current={currentLang} />
            </MenuItem>
        );
    }

    function renderMenuItems(): React.ReactNode[] {
        const items: React.ReactNode[] = [];

        if (driverinfosecurity.isAdmin()) {
            items.push(
                <MenuItem key="admin" onClick={() => setAnchorEl(null)} sx={{ p: 0, '&:hover': { bgcolor: 'transparent' } }}>
                    <NavLink to={'/administration'} style={{ color: 'white', width: '100%', padding: '6px 16px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                        <img style={{ marginRight: 12 }} src={new URL('../../img/menu-settings.png', import.meta.url).href} />
                        {i18next.t('navbar.administration')}
                    </NavLink>
                </MenuItem>
            );
            items.push(<Divider key="div-admin" sx={{ borderColor: 'rgba(255,255,255,0.3)', my: 0.5 }} />);
        }

        items.push(
            <Typography key="land-header" variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Land
            </Typography>
        );
        items.push(<MenuItem key="se" component="a" href="?country=se" sx={{ color: 'white', '&:hover': { color: 'secondary.light', bgcolor: 'transparent' } }}>🇸🇪 SE <Choose value="se" current={country} /></MenuItem>);
        items.push(<MenuItem key="dk" component="a" href="?country=dk" sx={{ color: 'white', '&:hover': { color: 'secondary.light', bgcolor: 'transparent' } }}>🇩🇰 DK <Choose value="dk" current={country} /></MenuItem>);
        items.push(<MenuItem key="no" component="a" href="?country=no" sx={{ color: 'white', '&:hover': { color: 'secondary.light', bgcolor: 'transparent' } }}>🇳🇴 NO <Choose value="no" current={country} /></MenuItem>);

        items.push(<Divider key="div-lang" sx={{ borderColor: 'rgba(255,255,255,0.3)', my: 0.5 }} />);
        items.push(
            <Typography key="lang-header" variant="caption" sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>
                Språk
            </Typography>
        );
        flags.forEach((lang) => items.push(renderLanguage(lang)));

        items.push(<Divider key="div-logout" sx={{ borderColor: 'rgba(255,255,255,0.3)', my: 0.5 }} />);
        items.push(
            <MenuItem key="logout" onClick={() => setAnchorEl(null)} sx={{ p: 0, '&:hover': { bgcolor: 'transparent' } }}>
                <NavLink to={'/Logout'} style={{ color: 'white', width: '100%', padding: '6px 16px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                    <img style={{ marginRight: 12 }} src={new URL('../../img/menu-logout.png', import.meta.url).href} />
                    {i18next.t('navbar.logout')}
                </NavLink>
            </MenuItem>
        );

        return items;
    }

    return (
        <AppBar position="static" color="primary" elevation={0} className="navbar">
            <Toolbar sx={{ minHeight: 56 }}>
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Link to={'/'}>
                        <img style={{ marginTop: -4, display: 'block' }} src={new URL('../../img/logo2.png', import.meta.url).href} />
                    </Link>
                    <Link to={'/'} style={{ color: 'white', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>
                        {i18next.t('navbar.start')}
                    </Link>
                </Box>

                <IconButton
                    id="navbarDropdown2"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    aria-controls={open ? 'nav-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open}
                    sx={{ color: 'white', borderRadius: 1, px: 1.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                    <img src={new URL('../../img/User.png', import.meta.url).href} />
                    <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />
                </IconButton>

                <Menu
                    id="nav-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    slotProps={{
                        paper: {
                            className: 'navbar-dropdown-menu',
                            sx: {
                                backgroundColor: 'primary.main',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.2)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                                minWidth: 200,
                                mt: 0.5
                            }
                        },
                        backdrop: { invisible: true }
                    }}
                >
                    {renderMenuItems()}
                </Menu>
            </Toolbar>
        </AppBar>
    );
}

export default withTranslation('translations')(NavMenu);
